import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from "@tanstack/react-table";

import dataTableStyle, { pillTones } from "./dataTableStyle.jsx";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Column defs may set `meta: { align: "right", variant: "mono" }` to opt into
// the money/reference-code styling described in the design spec.
function DataTable(props) {
  const {
    classes,
    columns,
    data,
    totalCount,
    pagination,
    onPaginationChange,
    sorting,
    onSortingChange,
    isLoading,
    emptyState,
    footer,
    onRowClick,
    title,
    subtitle,
    actions,
    toolbar,
    onSearch,
    searchValue,
    onFilterClick,
    onSortClick,
    onViewClick,
    children
  } = props;

  const manualSorting = sorting !== undefined && onSortingChange !== undefined;
  const manualPagination =
    pagination !== undefined && onPaginationChange !== undefined && totalCount !== undefined;

  const [internalSorting, setInternalSorting] = React.useState([]);
  const [internalPagination, setInternalPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10
  });

  const sortingState = manualSorting ? sorting : internalSorting;
  const paginationState = manualPagination ? pagination : internalPagination;

  const table = useReactTable({
    data,
    columns: columns || [],
    state: {
      sorting: sortingState,
      pagination: paginationState
    },
    manualSorting,
    manualPagination,
    ...(manualPagination
      ? { pageCount: Math.max(1, Math.ceil((totalCount || 0) / (paginationState.pageSize || 10))) }
      : {}),
    onSortingChange: manualSorting
      ? onSortingChange
      : updater =>
          setInternalSorting(old => (typeof updater === "function" ? updater(old) : updater)),
    onPaginationChange: manualPagination
      ? onPaginationChange
      : updater =>
          setInternalPagination(old => (typeof updater === "function" ? updater(old) : updater)),
    getCoreRowModel: getCoreRowModel(),
    ...(!manualSorting ? { getSortedRowModel: getSortedRowModel() } : {}),
    ...(!manualPagination ? { getPaginationRowModel: getPaginationRowModel() } : {})
  });

  const rows = table.getRowModel().rows || [];
  const leafColumns = table.getVisibleLeafColumns() || [];

  const [density, setDensity] = React.useState("comfortable"); // "comfortable" | "compact"
  const [showFilters, setShowFilters] = React.useState(true);
  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);

  const pageIndex = paginationState.pageIndex;
  const pageSize = paginationState.pageSize;
  const rowCount = manualPagination ? totalCount || 0 : data.length;
  const pageCount = manualPagination
    ? Math.max(1, Math.ceil(rowCount / pageSize))
    : Math.max(1, table.getPageCount());
  const from = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min(rowCount, (pageIndex + 1) * pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  const applyPagination = next => {
    if (manualPagination) onPaginationChange(next);
    else setInternalPagination(next);
  };
  const goPrevious = () => applyPagination({ ...paginationState, pageIndex: Math.max(0, pageIndex - 1) });
  const goNext = () =>
    applyPagination({ ...paginationState, pageIndex: Math.min(pageCount - 1, pageIndex + 1) });
  const changePageSize = e =>
    applyPagination({ pageIndex: 0, pageSize: Number(e.target.value) });

  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
    if (onFilterClick) onFilterClick(!showFilters);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
    if (onSortClick) onSortClick(event);
  };

  const handleViewToggle = () => {
    const nextDensity = density === "comfortable" ? "compact" : "comfortable";
    setDensity(nextDensity);
    if (onViewClick) onViewClick(nextDensity);
  };

  const showFooter = footer && !isLoading && rows.length > 0;

  return (
    <React.Fragment>
      {(toolbar || onSearch) && (
        <div className={classes.toolbar}>
          <div className={classes.toolbarLeft}>
            {onSearch && (
              <div className={classes.searchWrapper}>
                <Icon className={classes.searchIcon}>search</Icon>
                <input
                  type="text"
                  className={classes.searchInput}
                  placeholder="Search all columns..."
                  value={searchValue || ""}
                  onChange={e => onSearch(e.target.value)}
                />
              </div>
            )}
            {showFilters && toolbar}
          </div>
          <div className={classes.toolbarRight}>
            <div
              className={classNames(classes.toolbarItem, { [classes.toolbarItemActive]: showFilters })}
              onClick={handleFilterToggle}
            >
              <Icon fontSize="small">{showFilters ? "filter_list_off" : "filter_list"}</Icon>
              Filter
            </div>
            <div className={classes.toolbarItem} onClick={handleSortClick}>
              <Icon fontSize="small">sort</Icon>
              Sort
            </div>
            <div className={classes.toolbarItem} onClick={handleViewToggle}>
              <Icon fontSize="small">{density === "comfortable" ? "view_headline" : "view_comfy"}</Icon>
              {density === "comfortable" ? "Compact" : "Normal"}
            </div>
          </div>
        </div>
      )}

      <div className={classes.card}>
        {(title || subtitle || actions) && (
          <div className={classes.headerBar}>
            <div>
              {title && <h4 className={classes.headerTitle}>{title}</h4>}
              {subtitle && <p className={classes.headerSubtitle}>{subtitle}</p>}
            </div>
            {actions && <div className={classes.headerActions}>{actions}</div>}
          </div>
        )}

        {children ? children : (
          <div className={classNames(classes.tableWrap, { [classes.tableWrapCompact]: density === "compact" })}>
            <table className={classes.table}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      const meta = header.column.columnDef.meta || {};
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      const sortIcon =
                        sorted === "asc" ? "arrow_upward" : sorted === "desc" ? "arrow_downward" : "unfold_more";
                      return (
                        <th
                          key={header.id}
                          className={classNames(classes.th, {
                            [classes.thRight]: meta.align === "right",
                            [classes.thSortable]: canSort
                          })}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <Icon
                              className={classNames(classes.sortIcon, { [classes.sortIconActive]: !!sorted })}
                            >
                              {sortIcon}
                            </Icon>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              {isLoading ? (
                <tbody>
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <tr key={`skeleton-${rowIdx}`} className={classes.row}>
                      {leafColumns.map(col => (
                        <td key={col.id} className={classes.skeletonCell}>
                          <div className={classes.skeletonBar} style={{ width: `${60 + ((rowIdx * 13 + col.id.length * 7) % 30)}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ) : rows.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={leafColumns.length} className={classes.emptyState}>
                      {emptyState || "No records found."}
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.id}
                      className={classNames(classes.row, { [classes.rowClickable]: !!onRowClick })}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    >
                      {row.getVisibleCells().map(cell => {
                        const meta = cell.column.columnDef.meta || {};
                        return (
                          <td
                            key={cell.id}
                            className={classNames(classes.td, {
                              [classes.tdRight]: meta.align === "right",
                              [classes.tdMono]: meta.variant === "mono"
                            })}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              )}

              {showFooter && (
                <tfoot>
                  <tr className={classes.footerRow}>
                    {leafColumns.map((col, idx) => (
                      <td key={col.id} className={idx === 0 ? classes.footerLabelCell : classes.footerValueCell}>
                        {idx === 0 ? footer.label : (footer.values && footer.values[col.id]) || ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {!children && (
          <div className={classes.paginationBar}>
            <div className={classes.paginationLeft}>
              Rows per page
              {manualPagination ? (
                <span>{pageSize}</span>
              ) : (
                <select className={classes.pageSizeSelect} value={pageSize} onChange={changePageSize}>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className={classes.paginationRight}>
              <span>
                {from}–{to} of {rowCount}
              </span>
              <button className={classes.pageButton} onClick={goPrevious} disabled={!canPreviousPage}>
                <Icon>chevron_left</Icon>
              </button>
              <button className={classes.pageButton} onClick={goNext} disabled={!canNextPage}>
                <Icon>chevron_right</Icon>
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

DataTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.array,
  data: PropTypes.array,
  totalCount: PropTypes.number,
  pagination: PropTypes.shape({ pageIndex: PropTypes.number, pageSize: PropTypes.number }),
  onPaginationChange: PropTypes.func,
  sorting: PropTypes.array,
  onSortingChange: PropTypes.func,
  isLoading: PropTypes.bool,
  emptyState: PropTypes.node,
  footer: PropTypes.shape({ label: PropTypes.node, values: PropTypes.object }),
  onRowClick: PropTypes.func,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  toolbar: PropTypes.node,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  onFilterClick: PropTypes.func,
  onSortClick: PropTypes.func,
  onViewClick: PropTypes.func,
  children: PropTypes.node
};

DataTable.defaultProps = {
  data: [],
  columns: [],
  isLoading: false
};

export default withStyles(dataTableStyle)(DataTable);

const pillBaseStyle = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: "1.5"
};

export function Pill({ tone, children }) {
  const styleTones = pillTones[tone] || pillTones.muted;
  return (
    <span style={{ ...pillBaseStyle, background: styleTones.bg, color: styleTones.text }}>
      {children}
    </span>
  );
}
Pill.propTypes = { tone: PropTypes.oneOf(["success", "warning", "danger", "muted", "info"]), children: PropTypes.node };

export function TwoLineCell({ primary, secondary }) {
  return (
    <div>
      <div style={{ fontWeight: 500, fontSize: "13px", color: "#2c3345" }}>{primary}</div>
      {secondary && <div style={{ fontSize: "12px", color: "#999999", marginTop: "2px" }}>{secondary}</div>}
    </div>
  );
}
TwoLineCell.propTypes = { primary: PropTypes.node, secondary: PropTypes.node };

export function formatUGX(amount) {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat("en-UG").format(n);
}
